import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { PolicyTemplateService } from '../../../../services/entity-services/policy-template.service';
import { PolicyTemplateSummary } from '../../../../interfaces/policy-templates/policy-template-summary.interface';
import { PolicyTemplateDetail } from '../../../../interfaces/policy-templates/policy-template-detail.interface';
import {
  VmCleanupAction,
  UaCleanupAction,
  QuotaLimitAction,
  DestroyAction,
  ShutdownAction,
  DeallocateAction,
  QuotaExtendAction,
} from '../../../../interfaces/policy-templates/policy-cleanup-actions.interface';

@Component({
  selector: 'app-duplicate-policy-drawer',
  templateUrl: './duplicate-policy-drawer.component.html',
  styleUrls: ['./duplicate-policy-drawer.component.css']
})
export class DuplicatePolicyDrawerComponent {

  @Input() set policySummary(value: PolicyTemplateSummary | null) {
    this._summary = value;
    if (value) {
      this.isOpen = true;
      this.loadDetail(value.id);
    }
  }
  get policySummary(): PolicyTemplateSummary | null { return this._summary; }

  @Output() closed = new EventEmitter<void>();

  isOpen = false;
  isLoading = false;
  detail: PolicyTemplateDetail | null = null;

  private _summary: PolicyTemplateSummary | null = null;

  constructor(private readonly policyTemplateService: PolicyTemplateService) {}

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen) {
      this.close();
    }
  }

  close(): void {
    this.isOpen = false;
    this.detail = null;
    this._summary = null;
    this.closed.emit();
  }

  private loadDetail(id: string): void {
    this.isLoading = true;
    this.detail = null;
    this.policyTemplateService.getById(id).subscribe({
      next: (d) => { this.isLoading = false; this.detail = d; },
      error: () => { this.isLoading = false; }
    });
  }

  formatVmCleanup(action: VmCleanupAction): string {
    if (action.actionType === 'DoNothing') return 'Do Nothing';
    const gp = (action as ShutdownAction | DeallocateAction | DestroyAction).gracePeriod;
    const gpStr = `${gp.value} ${gp.unit}`;
    if (action.actionType === 'Shutdown')   return `Shutdown — grace period: ${gpStr}`;
    if (action.actionType === 'Deallocate') return `Deallocate — grace period: ${gpStr}`;
    const scope = (action as DestroyAction).scope === 'ComputeOnly'
      ? 'compute only' : 'compute + disk';
    return `Destroy (${scope}) — grace period: ${gpStr}`;
  }

  formatUaCleanup(action: UaCleanupAction): string {
    const map: Record<string, string> = {
      DoNothing: 'Do Nothing',
      DeactivateSubscription: 'Deactivate Subscription',
      DeleteResources: 'Delete Resources (subscription stays active)'
    };
    return map[action.actionType] ?? action.actionType;
  }

  formatQuotaAction(action: QuotaLimitAction): string {
    if (action.actionType === 'DeactivateSubscription') return 'Deactivate Subscription';
    if (action.actionType === 'DeleteResourcesAndSetReadOnly') return 'Delete Resources & Set Read-Only';
    return `Extend Budget by ₹${(action as QuotaExtendAction).additionalAmount}`;
  }

  get displayResourceType(): string {
    return (this.detail?.resourceType ?? this._summary?.resourceType) ?? '';
  }

  get displayPolicyName(): string {
    return this.detail?.policyName ?? this._summary?.policyName ?? 'Policy Details';
  }
}
